import { Dispatch } from 'redux';
import { IAppState } from './reducers';

const ADD_ASSET_DETAILS = 'ADD_ASSET_DETAILS';
const ADD_EDITABLE_ASSET_DETAILS = 'ADD_EDITABLE_ASSET_DETAILS';
const UPDATE_MEDIA_INFORMATION = 'UPDATE_MEDIA_INFORMATION';
const UPDATE_VIDEO_TIME_STAMP = 'UPDATE_VIDEO_TIME_STAMP';
const RESET_VIDEO_TIME_STAMP = 'RESET_VIDEO_TIME_STAMP';
const RESET_ASSET_DETAILS = 'RESET_ASSET_DETAILS';
const RESET_EDITABLE_ASSET_DETAILS = 'RESET_EDITABLE_ASSET_DETAILS';

export const DEFAULT_MESSAGE = 'Media content unavailable.';
export const INVALID_VIDEO_MESSAGE = 'Video content unavailable.';

const MEDIA_INFORMATION = 'catapult:mediaInformation';

interface MediaInformation {
  type: string | null;
  source: string | null;
  sourceSource: string | null;
  message: string;
  timeStamp?: number;
}

interface PreviewAssetState {
  isLoaded: boolean;
  mediaInformation: MediaInformation;
  assetDetails: any[];
  editableAssetDetails: any[];
  timeStamp: number;
}

type PreviewAssetAction =
  | { type: typeof ADD_ASSET_DETAILS; payload: any[] }
  | { type: typeof ADD_EDITABLE_ASSET_DETAILS; payload: any[] }
  | { type: typeof RESET_ASSET_DETAILS; payload: any[] }
  | { type: typeof RESET_EDITABLE_ASSET_DETAILS; payload: any[] }
  | { type: typeof UPDATE_MEDIA_INFORMATION; payload: MediaInformation }
  | { type: typeof UPDATE_VIDEO_TIME_STAMP; payload: number }
  | { type: typeof RESET_VIDEO_TIME_STAMP; payload: number };

const INITIAL_STATE: PreviewAssetState = {
  isLoaded: false,
  mediaInformation: {
    type: null,
    source: null,
    sourceSource: null,
    message: DEFAULT_MESSAGE,
  },
  assetDetails: [],
  editableAssetDetails: [],
  timeStamp: 0,
};

export default function reducer(
  state: PreviewAssetState = INITIAL_STATE,
  action: PreviewAssetAction,
): PreviewAssetState {
  switch (action.type) {
    case ADD_ASSET_DETAILS:
      return {
        ...state,
        assetDetails: action.payload,
        isLoaded: true,
      };
    case ADD_EDITABLE_ASSET_DETAILS:
      return {
        ...state,
        editableAssetDetails: action.payload,
      };
    case RESET_ASSET_DETAILS:
      return {
        ...state,
        isLoaded: false,
        assetDetails: action.payload,
      };
    case RESET_EDITABLE_ASSET_DETAILS:
      return {
        ...state,
        editableAssetDetails: action.payload,
      };
    case UPDATE_MEDIA_INFORMATION:
      return {
        ...state,
        mediaInformation: action.payload,
      };
    case UPDATE_VIDEO_TIME_STAMP:
      return {
        ...state,
        timeStamp: action.payload,
      };
    case RESET_VIDEO_TIME_STAMP:
      return {
        ...state,
        timeStamp: action.payload,
      };
    default:
      return state;
  }
}

export function addAssetDetails(assetDetails: any[]): PreviewAssetAction {
  return {
    type: ADD_ASSET_DETAILS,
    payload: assetDetails,
  };
}

export function addEditableAssetDetails(editableAssetDetails: any[]): PreviewAssetAction {
  return {
    type: ADD_EDITABLE_ASSET_DETAILS,
    payload: editableAssetDetails,
  };
}

export function resetAssetDetails(): PreviewAssetAction {
  return {
    type: RESET_ASSET_DETAILS,
    payload: [],
  };
}

export function resetEditableAssetDetails(): PreviewAssetAction {
  return {
    type: RESET_EDITABLE_ASSET_DETAILS,
    payload: [],
  };
}

export function setMediaInformation() {
  return (dispatch: any, getState: any) => {
    const mediaInformation: MediaInformation = JSON.parse(localStorage.getItem(MEDIA_INFORMATION) || '{}');
    dispatch({
      type: UPDATE_MEDIA_INFORMATION,
      payload: mediaInformation,
    });

    if (mediaInformation.timeStamp) {
      dispatch({
        type: UPDATE_VIDEO_TIME_STAMP,
        payload: mediaInformation.timeStamp,
      });
    }
  };
}

export const updateMediaInformation = (mediaInformation: MediaInformation) => (dispatch: Dispatch<any>, getState: () => IAppState) => {
  localStorage.setItem(MEDIA_INFORMATION, JSON.stringify(mediaInformation));
  const { previewAsset } = getState();
  if (previewAsset.assetDetails.length > 0) {
    dispatch({
      type: UPDATE_MEDIA_INFORMATION,
      payload: mediaInformation,
    });
  }
};

export function updateVideoTimeStamp(timeStamp: number): PreviewAssetAction {
  // Save timestamp in local storage
  const mediaInformation: MediaInformation = JSON.parse(localStorage.getItem(MEDIA_INFORMATION) || '{}');
  localStorage.setItem(MEDIA_INFORMATION, JSON.stringify({ ...mediaInformation, timeStamp }));
  return {
    type: UPDATE_VIDEO_TIME_STAMP,
    payload: timeStamp,
  };
}

export function resetVideoTimeStamp(): PreviewAssetAction {
  return {
    type: RESET_VIDEO_TIME_STAMP,
    payload: 0,
  };
}

export function selectPreviewedAssetSource(state: { previewAsset: PreviewAssetState }): string | null {
  return state.previewAsset.mediaInformation.source;
}
